import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Nav from "@/components/Nav";
import { RefreshCw, LogOut, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatErr = (d) => {
  if (d == null) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(d);
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("gv_admin_token") || null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (tok) => {
      try {
        const res = await axios.get(`${API}/access/requests`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setData(res.data);
        setError(null);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("gv_admin_token");
          setToken(null);
        } else {
          setError("Failed to load access requests");
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("gv_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setAuthError(formatErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gv_admin_token");
    setToken(null);
    setData(null);
  };

  const [keys, setKeys] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [issuedKey, setIssuedKey] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const loadKeys = useCallback(
    async (tok) => {
      try {
        const res = await axios.get(`${API}/tenants/keys`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setKeys(res.data.keys);
      } catch {
        setKeys(null);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadKeys(token);
  }, [token, loadKeys]);

  const [deliveries, setDeliveries] = useState(null);

  const loadDeliveries = useCallback(
    async (tok) => {
      try {
        const res = await axios.get(`${API}/webhooks/deliveries`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setDeliveries(res.data.deliveries);
      } catch {
        setDeliveries(null);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadDeliveries(token);
  }, [token, loadDeliveries]);

  const resendDelivery = async (deliveryId) => {
    try {
      await axios.post(
        `${API}/webhooks/deliveries/${deliveryId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadDeliveries(token);
    } catch {
      /* row refresh on next load */
    }
  };

  const updateLimit = async (tenantId, perMinute) => {
    try {
      await axios.put(
        `${API}/tenants/keys/${tenantId}/limit`,
        { per_minute: Number(perMinute) || 60 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadKeys(token);
    } catch {
      /* table refresh on next load */
    }
  };

  const updateBillingEmail = async (tenantId, email) => {
    try {
      await axios.put(
        `${API}/tenants/keys/${tenantId}/billing-email`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadKeys(token);
    } catch {
      /* table refresh on next load */
    }
  };

  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [billingFor, setBillingFor] = useState(null);

  const loadBilling = useCallback(async (tok) => {
    try {
      const [u, i] = await Promise.all([
        axios.get(`${API}/billing/usage`, { headers: { Authorization: `Bearer ${tok}` } }),
        axios.get(`${API}/billing/invoices`, { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      setBilling(u.data);
      setInvoices(i.data.invoices);
    } catch {
      setBilling(null);
    }
  }, []);

  useEffect(() => {
    if (token) loadBilling(token);
  }, [token, loadBilling]);

  const generateInvoice = async (tenantId) => {
    setBillingFor(tenantId);
    try {
      await axios.post(
        `${API}/billing/invoice/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      /* no usage or Stripe error — list refresh shows state */
    } finally {
      setBillingFor(null);
      loadBilling(token);
    }
  };

  const issueKey = async (e) => {
    e.preventDefault();
    setIssuing(true);
    try {
      const res = await axios.post(
        `${API}/tenants/keys`,
        { name: keyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIssuedKey(res.data);
      setKeyName("");
      loadKeys(token);
    } catch {
      setIssuedKey(null);
    } finally {
      setIssuing(false);
    }
  };

  const revokeKey = async (tenantId) => {
    await axios.post(
      `${API}/tenants/keys/${tenantId}/revoke`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    loadKeys(token);
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="bg-background text-foreground min-h-screen">
        <Nav />
        <main className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 pb-24 flex justify-center">
          <form
            data-testid="admin-login-form"
            onSubmit={login}
            className="w-full max-w-md border border-border bg-card p-10"
          >
            <span className="w-10 h-10 border border-foreground flex items-center justify-center mb-8">
              <Lock className="w-5 h-5" />
            </span>
            <h1 className="font-display font-black tracking-tight text-3xl">Admin Access</h1>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-3 mb-8">
              Internal intake — authorized staff only
            </p>
            <label htmlFor="admin-email" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground transition-colors duration-200 mb-5"
            />
            <label htmlFor="admin-password" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground transition-colors duration-200 mb-6"
            />
            {authError && (
              <p data-testid="admin-login-error" className="font-mono text-xs text-anomaly border border-anomaly px-4 py-3 mb-6">
                {authError}
              </p>
            )}
            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-foreground text-background font-mono text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-background text-foreground min-h-screen">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-4">Internal · Intake</p>
            <h1 className="font-display font-black tracking-tight text-5xl leading-none">Access Requests</h1>
            <p className="font-mono text-xs text-muted-foreground mt-4 tracking-widest">
              {data ? `${data.total} SUBMISSION${data.total === 1 ? "" : "S"}` : "LOADING…"}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="border border-border font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 flex items-center gap-2 hover:border-foreground transition-colors duration-200"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="border border-border font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 flex items-center gap-2 hover:border-anomaly hover:text-anomaly transition-colors duration-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          </div>
        </div>
        {error && (
          <p data-testid="admin-error" className="font-mono text-xs text-anomaly border border-anomaly px-4 py-3">{error}</p>
        )}
        {data && (
          <div className="border border-border bg-card overflow-x-auto">
            <table data-testid="admin-table" className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-border bg-muted/50 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground text-left">
                  <th className="px-5 py-3.5 font-medium">Reference</th>
                  <th className="px-5 py-3.5 font-medium">Name</th>
                  <th className="px-5 py-3.5 font-medium">Email</th>
                  <th className="px-5 py-3.5 font-medium">Company</th>
                  <th className="px-5 py-3.5 font-medium">Use Case</th>
                  <th className="px-5 py-3.5 font-medium">Submitted</th>
                  <th className="px-5 py-3.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.requests.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center font-mono text-xs text-muted-foreground tracking-widest">
                      NO REQUESTS YET
                    </td>
                  </tr>
                )}
                {data.requests.map((r) => (
                  <tr key={r.request_id} data-testid={`admin-row-${r.request_id}`} className="hover:bg-muted/40 transition-colors duration-150">
                    <td className="px-5 py-4 font-mono text-xs">{r.request_id}</td>
                    <td className="px-5 py-4 font-medium">{r.name}</td>
                    <td className="px-5 py-4 font-mono text-xs">{r.email}</td>
                    <td className="px-5 py-4">{r.company}</td>
                    <td className="px-5 py-4 text-muted-foreground max-w-[260px] truncate">{r.use_case || "—"}</td>
                    <td className="px-5 py-4 font-mono text-xs text-muted-foreground">
                      {new Date(r.submitted_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 bg-telemetry text-white">
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-20">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-4">Tenants · Real Mode</p>
          <h2 className="font-display font-black tracking-tight text-4xl leading-none mb-10">API Keys</h2>

          <form data-testid="issue-key-form" onSubmit={issueKey} className="border border-border bg-card p-6 flex flex-wrap items-end gap-4 mb-4">
            <div className="flex-1 min-w-[240px]">
              <label htmlFor="key-name" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                Tenant Name
              </label>
              <input
                id="key-name"
                data-testid="key-name-input"
                required
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="Acme Last-Mile Inc."
                className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground transition-colors duration-200"
              />
            </div>
            <button
              data-testid="issue-key-btn"
              type="submit"
              disabled={issuing}
              className="bg-foreground text-background font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
            >
              {issuing ? "Issuing…" : "Issue Key"}
            </button>
          </form>

          {issuedKey && (
            <div data-testid="issued-key-banner" className="border border-truth bg-card p-6 mb-8">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-truth mb-3">
                Key issued for {issuedKey.name} — shown once, store it now
              </p>
              <p data-testid="issued-key-value" className="font-mono text-sm bg-muted px-4 py-3 break-all select-all">
                {issuedKey.key}
              </p>
            </div>
          )}

          {keys && (
            <div className="border border-border bg-card overflow-x-auto">
              <table data-testid="keys-table" className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground text-left">
                    <th className="px-5 py-3.5 font-medium">Tenant</th>
                    <th className="px-5 py-3.5 font-medium">Name</th>
                    <th className="px-5 py-3.5 font-medium">Billing Email</th>
                    <th className="px-5 py-3.5 font-medium">Key Prefix</th>
                    <th className="px-5 py-3.5 font-medium">Created</th>
                    <th className="px-5 py-3.5 font-medium">Usage (Month)</th>
                    <th className="px-5 py-3.5 font-medium">Limit /min</th>
                    <th className="px-5 py-3.5 font-medium">Status</th>
                    <th className="px-5 py-3.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {keys.map((k) => (
                    <tr key={k.tenant_id} data-testid={`key-row-${k.tenant_id}`} className="hover:bg-muted/40 transition-colors duration-150">
                      <td className="px-5 py-4 font-mono text-xs">{k.tenant_id}</td>
                      <td className="px-5 py-4 font-medium">{k.name}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`billing-email-${k.tenant_id}`}
                            type="email"
                            defaultValue={k.billing_email || ""}
                            placeholder="billing@tenant.com"
                            className="w-44 bg-background border border-border px-2 py-1.5 font-mono text-xs focus:outline-none focus:border-foreground transition-colors duration-200"
                          />
                          <button
                            data-testid={`billing-email-save-${k.tenant_id}`}
                            onClick={(e) =>
                              updateBillingEmail(k.tenant_id, e.currentTarget.previousSibling.value)
                            }
                            className="font-mono text-[10px] tracking-wider uppercase border border-border px-2.5 py-1.5 hover:border-foreground transition-colors duration-200"
                          >
                            Set
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{k.key_prefix}</td>
                      <td className="px-5 py-4 font-mono text-xs text-muted-foreground">
                        {new Date(k.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs" data-testid={`usage-${k.tenant_id}`}>
                        {(k.usage_this_month ?? 0).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`limit-input-${k.tenant_id}`}
                            type="number"
                            min={1}
                            defaultValue={k.rate_limit_per_minute ?? 60}
                            className="w-20 bg-background border border-border px-2 py-1.5 font-mono text-xs focus:outline-none focus:border-foreground transition-colors duration-200"
                          />
                          <button
                            data-testid={`limit-save-${k.tenant_id}`}
                            onClick={(e) =>
                              updateLimit(k.tenant_id, e.currentTarget.previousSibling.value)
                            }
                            className="font-mono text-[10px] tracking-wider uppercase border border-border px-2.5 py-1.5 hover:border-foreground transition-colors duration-200"
                          >
                            Set
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 ${k.revoked ? "bg-anomaly text-white" : "bg-truth text-white"}`}>
                          {k.revoked ? "Revoked" : "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {!k.revoked && (
                          <button
                            data-testid={`revoke-${k.tenant_id}`}
                            onClick={() => revokeKey(k.tenant_id)}
                            className="font-mono text-[10px] tracking-[0.2em] uppercase border border-border px-4 py-2 hover:border-anomaly hover:text-anomaly transition-colors duration-200"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-20">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-4">Billing · Usage-Metered</p>
          <h2 className="font-display font-black tracking-tight text-4xl leading-none mb-10">Invoicing</h2>

          {billing && (
            <div className="border border-border bg-card overflow-x-auto mb-10">
              <table data-testid="billing-table" className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground text-left">
                    <th className="px-5 py-3.5 font-medium">Tenant</th>
                    <th className="px-5 py-3.5 font-medium">Events ({billing.month})</th>
                    <th className="px-5 py-3.5 font-medium">Estimated (Tiered)</th>
                    <th className="px-5 py-3.5 font-medium">Stripe Customer</th>
                    <th className="px-5 py-3.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {billing.tenants.map((t) => (
                    <tr key={t.tenant_id} data-testid={`billing-row-${t.tenant_id}`} className="hover:bg-muted/40 transition-colors duration-150">
                      <td className="px-5 py-4 font-medium">{t.name}</td>
                      <td className="px-5 py-4 font-mono text-xs">{t.events_this_month.toLocaleString()}</td>
                      <td className="px-5 py-4 font-mono text-xs">
                        ${(t.estimated_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-muted-foreground">
                        {t.stripe_customer_id || "— (created on first invoice)"}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {t.events_this_month > 0 && !t.revoked && (
                          <button
                            data-testid={`invoice-${t.tenant_id}`}
                            onClick={() => generateInvoice(t.tenant_id)}
                            disabled={billingFor === t.tenant_id}
                            className="font-mono text-[10px] tracking-[0.15em] uppercase bg-foreground text-background px-4 py-2 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
                          >
                            {billingFor === t.tenant_id ? "Generating…" : "Generate Invoice"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {invoices && invoices.length > 0 && (
            <div className="border border-border bg-card overflow-x-auto">
              <table data-testid="invoices-table" className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground text-left">
                    <th className="px-5 py-3.5 font-medium">Invoice</th>
                    <th className="px-5 py-3.5 font-medium">Tenant</th>
                    <th className="px-5 py-3.5 font-medium">Month</th>
                    <th className="px-5 py-3.5 font-medium">Events</th>
                    <th className="px-5 py-3.5 font-medium">Amount</th>
                    <th className="px-5 py-3.5 font-medium">Status</th>
                    <th className="px-5 py-3.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="hover:bg-muted/40 transition-colors duration-150">
                      <td className="px-5 py-4 font-mono text-xs">{inv.invoice_id}</td>
                      <td className="px-5 py-4 font-medium">{inv.tenant_id}</td>
                      <td className="px-5 py-4 font-mono text-xs">{inv.month}</td>
                      <td className="px-5 py-4 font-mono text-xs">{inv.events.toLocaleString()}</td>
                      <td className="px-5 py-4 font-mono text-xs">
                        ${(inv.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 bg-telemetry text-white">
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <a
                          data-testid={`invoice-link-${inv.invoice_id}`}
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-[10px] tracking-[0.15em] uppercase border border-border px-4 py-2 hover:border-telemetry hover:text-telemetry transition-colors duration-200 inline-block"
                        >
                          Open Invoice
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="font-mono text-[10px] tracking-wider text-muted-foreground mt-4">
            STRIPE TEST MODE — invoices are payable via hosted link with test card 4242 4242 4242 4242
          </p>
        </div>

        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-4">Webhooks · Delivery Log</p>
              <h2 className="font-display font-black tracking-tight text-4xl leading-none">Deliveries</h2>
              <p className="font-mono text-xs text-muted-foreground mt-4 tracking-widest">
                {deliveries ? `${deliveries.length} ATTEMPT${deliveries.length === 1 ? "" : "S"} LOGGED` : "LOADING…"}
              </p>
            </div>
            <button
              data-testid="deliveries-refresh-btn"
              onClick={() => loadDeliveries(token)}
              className="border border-border font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 flex items-center gap-2 hover:border-foreground transition-colors duration-200"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
          {deliveries && (
            <div className="border border-border bg-card overflow-x-auto">
              <table data-testid="deliveries-table" className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground text-left">
                    <th className="px-5 py-3.5 font-medium">Time</th>
                    <th className="px-5 py-3.5 font-medium">Tenant</th>
                    <th className="px-5 py-3.5 font-medium">Event Type</th>
                    <th className="px-5 py-3.5 font-medium">Event ID</th>
                    <th className="px-5 py-3.5 font-medium">Endpoint</th>
                    <th className="px-5 py-3.5 font-medium">HTTP</th>
                    <th className="px-5 py-3.5 font-medium">Attempts</th>
                    <th className="px-5 py-3.5 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {deliveries.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-5 py-12 text-center font-mono text-xs text-muted-foreground tracking-widest">
                        NO DELIVERIES YET — REGISTER A WEBHOOK AND VERIFY AN EVENT
                      </td>
                    </tr>
                  )}
                  {deliveries.map((d) => {
                    const retrying = !d.ok && !!d.next_retry_at;
                    return (
                      <tr key={d.delivery_id} data-testid={`delivery-row-${d.delivery_id}`} className="hover:bg-muted/40 transition-colors duration-150">
                        <td className="px-5 py-4 font-mono text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(d.last_attempt_at || d.delivered_at).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 font-medium">{d.tenant_name || d.tenant_id}</td>
                        <td className="px-5 py-4 font-mono text-xs text-telemetry">{d.event_type}</td>
                        <td className="px-5 py-4 font-mono text-xs">{d.event_id}</td>
                        <td className="px-5 py-4 font-mono text-xs text-muted-foreground max-w-[220px] truncate">{d.url}</td>
                        <td className="px-5 py-4 font-mono text-xs">{d.status_code || "—"}</td>
                        <td className="px-5 py-4 font-mono text-xs">{d.attempts}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              data-testid={`delivery-status-${d.delivery_id}`}
                              className={`font-mono text-[10px] tracking-widest uppercase px-2.5 py-1 ${
                                d.ok ? "bg-truth text-white" : retrying ? "bg-telemetry text-white" : "bg-anomaly text-white"
                              }`}
                            >
                              {d.ok ? "Delivered" : retrying ? "Retrying" : "Failed"}
                            </span>
                            {!d.ok && (
                              <button
                                data-testid={`resend-${d.delivery_id}`}
                                onClick={() => resendDelivery(d.delivery_id)}
                                className="font-mono text-[10px] tracking-[0.15em] uppercase border border-border px-3 py-1 hover:border-foreground transition-colors duration-200"
                              >
                                Resend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="font-mono text-[10px] tracking-wider text-muted-foreground mt-4">
            FAILED DELIVERIES RETRY AUTOMATICALLY AT +10s, +30s, +90s BEFORE BEING MARKED FAILED
          </p>
        </div>
      </main>
    </div>
  );
};

export default Admin;
